// Small pieces the Team screens share: the role chip (TeamMembersView `teamRoleColor`), the account status badge, the
// "last seen" line, activity type glyphs (ActivityType.systemImage / tint) and a copy-to-clipboard helper.

import {
  ArrowRightLeft,
  Camera,
  CircleCheck,
  CirclePlus,
  FileText,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  StickyNote,
  UserPlus,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { USER_ROLE_TITLES, type ActivityType } from '@/contracts/enums';
import type { Role } from '@/contracts/wire';
import { formatElapsed, formatShortDateTime } from '@/domain/format';
import type { Presence } from '@/domain/team';
import { Badge, Chip, ColorDot, GT_COLORS, PRESENCE_COLORS, PRESENCE_TITLES, type BadgeTone, useToast } from '@/ui';
import { ACCOUNT_STATUS_TITLES, type AccountStatus } from './members';

/** `teamRoleColor`: admin orange, manager purple, rep blue. */
export const ROLE_COLORS: Readonly<Record<Role, string>> = {
  admin: GT_COLORS.orange,
  manager: GT_COLORS.purple,
  rep: GT_COLORS.blue,
};

/** Role chip; admins filled, as in the iOS member row. */
export function RoleChip({ role, size }: { role: Role; size?: 'xs' | 'sm' | 'md' }) {
  return (
    <Chip color={ROLE_COLORS[role]} filled={role === 'admin'} size={size}>
      {USER_ROLE_TITLES[role] ?? role}
    </Chip>
  );
}

const ACCOUNT_TONES: Readonly<Record<AccountStatus, BadgeTone>> = {
  signedUp: 'success',
  invited: 'warning',
  expired: 'danger',
  suspended: 'danger',
  none: 'neutral',
};

export function AccountBadge({ status }: { status: AccountStatus }) {
  return (
    <Badge tone={ACCOUNT_TONES[status]} size="sm">
      {ACCOUNT_STATUS_TITLES[status]}
    </Badge>
  );
}

/**
 * A profile written by a newer app (data/README.md): shown so nobody goes missing, never edited or invited from here.
 * Same words as the Stores marker.
 */
export function NewerAppBadge() {
  return (
    <Badge
      tone="warning"
      size="xs"
      title="Created by a newer version of Ground Team. Some details may be missing here until the console is updated."
    >
      Newer app
    </Badge>
  );
}

/** Presence dot and "Online" / "Away · 2 hours ago" / "3 days ago" / "No location yet". */
export function LastSeen({ at, presence, now }: { at: Date | null; presence: Presence; now: Date }) {
  if (!at) return <span className="text-fg-subtle">No location yet</span>;
  const relative = formatElapsed(at, now);
  const text = presence === 'offline' ? relative : `${PRESENCE_TITLES[presence]} · ${relative}`;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5" title={`Last location ${formatShortDateTime(at, now)}`}>
      <ColorDot color={PRESENCE_COLORS[presence]} size={8} />
      <span className="truncate">{text}</span>
    </span>
  );
}

/** ActivityType.systemImage as lucide glyphs, with ActivityType.tint (Components+Store.swift). */
export const ACTIVITY_GLYPHS: Readonly<Record<ActivityType, { icon: ReactNode; color: string }>> = {
  visit: { icon: <MapPin />, color: GT_COLORS.orange },
  call: { icon: <Phone />, color: GT_COLORS.green },
  email: { icon: <Mail />, color: GT_COLORS.blue },
  text: { icon: <MessageSquare />, color: GT_COLORS.teal },
  note: { icon: <StickyNote />, color: GT_COLORS.grey },
  meeting: { icon: <Users />, color: GT_COLORS.purple },
  task: { icon: <CircleCheck />, color: GT_COLORS.navy },
  stageChange: { icon: <ArrowRightLeft />, color: GT_COLORS.amber },
  assignment: { icon: <UserPlus />, color: GT_COLORS.navy },
  created: { icon: <CirclePlus />, color: GT_COLORS.green },
  photo: { icon: <Camera />, color: GT_COLORS.purple },
  document: { icon: <FileText />, color: GT_COLORS.grey },
};

/**
 * Copies text and remembers which button did it for 1.5 s ("Copied" feedback, as InviteCodeCard). A browser that
 * refuses (no permission, insecure context) gets a toast instead of silence.
 */
export function useCopy(): { copied: string | null; copy: (text: string, key: string) => Promise<void> } {
  const toast = useToast();
  const [copied, setCopied] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = useCallback(
    async (text: string, key: string) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(key);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(null), 1500);
      } catch {
        toast.error("Couldn't copy. Select the text and copy it yourself.");
      }
    },
    [toast],
  );
  return { copied, copy };
}
